import {
  IconFormatAlignCenterMS,
  IconFormatAlignJustifyMS,
  IconFormatAlignLeftMS,
  IconFormatAlignRightMS,
  IconFormatBoldMS,
  IconFormatClearMS,
  IconFormatColorTextMS,
  IconFormatItalicMS,
  IconFormatListBulletedMS,
  IconFormatListNumberedMS,
  IconFormatStrikethroughMS,
  IconFormatUnderlinedMS,
  IconInkHighlighterMS,
  IconLinkMS,
} from '@/constants/icons';
import { Popover, TextField } from '@mui/material';
import { Editor } from '@tiptap/react';
import { MouseEvent, useRef, useState } from 'react';

import {
  ApplyLinkButton,
  Divider,
  LinkPopoverContent,
  ToolbarButton,
  ToolbarContainer,
} from './styles';

interface ToolbarProps {
  editor: Editor | null;
}

export function Toolbar({ editor }: ToolbarProps): JSX.Element | null {
  const colorInputRef = useRef<HTMLInputElement>(null);
  const highlightInputRef = useRef<HTMLInputElement>(null);
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');

  if (!editor) return null;

  const handleOpenLink = (event: MouseEvent<HTMLElement>): void => {
    setLinkUrl((editor.getAttributes('link').href as string) ?? '');
    setLinkAnchor(event.currentTarget);
  };

  const handleApplyLink = (): void => {
    if (linkUrl.trim()) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl.trim() })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setLinkAnchor(null);
  };

  return (
    <ToolbarContainer>
      <ToolbarButton
        selected={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        {IconFormatBoldMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        {IconFormatItalicMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        {IconFormatUnderlinedMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        {IconFormatStrikethroughMS}
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        onClick={() =>
          editor.chain().focus().unsetAllMarks().clearNodes().run()
        }
      >
        {IconFormatClearMS}
      </ToolbarButton>

      <Divider />

      <ToolbarButton onClick={() => colorInputRef.current?.click()}>
        {IconFormatColorTextMS}
      </ToolbarButton>
      <input
        ref={colorInputRef}
        type="color"
        hidden
        onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
      />
      <ToolbarButton
        selected={editor.isActive('highlight')}
        onClick={() => highlightInputRef.current?.click()}
      >
        {IconInkHighlighterMS}
      </ToolbarButton>
      <input
        ref={highlightInputRef}
        type="color"
        hidden
        onChange={(e) =>
          editor
            .chain()
            .focus()
            .toggleHighlight({ color: e.target.value })
            .run()
        }
      />

      <Divider />

      <ToolbarButton
        selected={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        {IconFormatListNumberedMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        {IconFormatListBulletedMS}
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        selected={editor.isActive('link')}
        onClick={handleOpenLink}
      >
        {IconLinkMS}
      </ToolbarButton>
      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <LinkPopoverContent>
          <TextField
            size="small"
            label="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleApplyLink();
            }}
          />
          <ApplyLinkButton onClick={handleApplyLink}>aplicar</ApplyLinkButton>
        </LinkPopoverContent>
      </Popover>

      <Divider />

      <ToolbarButton
        selected={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        {IconFormatAlignLeftMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        {IconFormatAlignCenterMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        {IconFormatAlignRightMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive({ textAlign: 'justify' })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
      >
        {IconFormatAlignJustifyMS}
      </ToolbarButton>
    </ToolbarContainer>
  );
}
